import { motion } from "framer-motion";
import { Wrench, Fuel } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const rows = [
  { task: "Engine service", w: "w-3/4" },
  { task: "Hull & deck upkeep", w: "w-1/2" },
  { task: "Safety equipment", w: "w-2/5" },
  { task: "Electrical checks", w: "w-1/4" },
];

const MaintenanceMock = () => (
  <div className="mt-8 rounded-2xl border border-[#1E3E62]/10 bg-[#F9FAFC] p-5">
    <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">
      <span>Work</span><span>Cost</span>
    </div>
    <ul className="mt-4 space-y-4">
      {rows.map((r, i) => (
        <li key={r.task} className="grid grid-cols-[1fr_1fr] items-center gap-4">
          <span className="text-sm text-[#0B192C]">{r.task}</span>
          <div className="h-2 rounded-full bg-[#1E3E62]/10">
            <motion.div
              className={`h-2 rounded-full bg-[#1E3E62] ${r.w}`}
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1, delay: 0.2 + i * 0.1 }}
              style={{ originX: 0 }}
            />
          </div>
        </li>
      ))}
    </ul>
  </div>
);

const path = "M0 70 L40 64 L80 68 L120 60 L160 66 L200 62 L240 22 L280 60 L320 64";

const ConsumablesMock = () => (
  <div className="mt-8 rounded-2xl border border-[#F4EDE3]/15 bg-[#10223a] p-5">
    <div className="flex justify-between font-mono text-[11px] uppercase tracking-[0.2em] text-slate-300">
      <span>Usage over time</span>
      <span className="text-[#D4A373]">Flagged</span>
    </div>
    <svg viewBox="0 0 320 90" className="mt-4 w-full" aria-hidden="true">
      <line x1="0" y1="45" x2="320" y2="45" stroke="#F4EDE3" strokeOpacity="0.15" strokeDasharray="4 4" />
      <motion.path
        d={path}
        fill="none"
        stroke="#F4EDE3"
        strokeWidth="2.5"
        strokeLinejoin="round"
        initial={{ pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={{ once: true, amount: 0.1 }}
        transition={{ duration: 1.6, ease: "easeInOut" }}
      />
      <circle cx="240" cy="22" r="11" fill="#C87D55" fillOpacity="0.25" className="pulse-dot" />
      <circle cx="240" cy="22" r="5" fill="#C87D55" />
    </svg>
    <p className="mt-3 text-sm text-slate-300">Unusual consumption, flagged.</p>
  </div>
);

const Card = ({ testId, dark, icon: Icon, title, line, children }) => (
  <div data-testid={testId} className={`h-full rounded-[28px] p-8 md:p-10 ${dark ? "bg-[#0B192C] text-[#F4EDE3]" : "border border-[#1E3E62]/10 bg-white text-[#0B192C]"}`}>
    <Icon className={`h-6 w-6 ${dark ? "text-[#D4A373]" : "text-[#C87D55]"}`} />
    <h3 className="mt-6 font-display text-3xl leading-tight md:text-4xl">{title}</h3>
    <p className={`mt-3 text-base ${dark ? "text-slate-300" : "text-slate-600"}`}>{line}</p>
    {children}
  </div>
);

export const Proof = () => (
  <section data-testid="proof-section" className="bg-[#F4EDE3] px-6 py-24 md:px-10 md:py-36">
    <div className="mx-auto max-w-7xl">
      <Reveal>
        <Eyebrow>Proof</Eyebrow>
        <h2 className="mt-5 max-w-3xl font-display text-4xl leading-[1.05] tracking-tight text-[#0B192C] md:text-6xl">
          See more. <span className="italic text-[#C87D55]">Sooner.</span>
        </h2>
      </Reveal>
      <div className="mt-16 grid gap-6 lg:grid-cols-2">
        <Reveal className="h-full">
          <Card testId="proof-point-maintenance-card" icon={Wrench} title="Every job. Every cost. One view." line="Maintenance and spend, side by side.">
            <MaintenanceMock />
          </Card>
        </Reveal>
        <Reveal delay={0.15} className="h-full">
          <Card testId="proof-point-consumables-card" dark icon={Fuel} title="Odd usage? Caught early." line="Consumables tracked. Spikes flagged.">
            <ConsumablesMock />
          </Card>
        </Reveal>
      </div>
      <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.2em] text-slate-600">Illustrative examples · sample data, not real vessel data</p>
    </div>
  </section>
);
